import { useParams } from "react-router-dom";
import Query from "../common/Query";
import { FormContainerConfig } from "./types";
import FormContainer from "./components/FormContainer";
import FORM_QUERY from "./queries/FORM_QUERY";
import FORMAUTH_QUERY from "./queries/FORMAUTH_QUERY";
import useIsAuthorized from "../hooks/useIsAuthorized";
import { Button } from "react-bootstrap";
import { loginRequest } from "../utils/authConfig";
import { FormikHelpers } from "formik";
import SUBMITFORM_MUTATION from "./queries/SUBMITFORM_MUTATION";
import SUBMITFORMAUTH_MUTATION from "./queries/SUBMITFORMAUTH_MUTATION";
import FILE_UPLOAD_MUTATION from "./queries/FILE_UPLOAD_MUTATION";
import { useMutation } from "@apollo/client";
import Loading from "../common/Loading";
import AlertDismissible from "../common/AlertDismissible";
import { useContext, useState } from "react";
import { FormProps } from "formik-tastic";
import { AppContext } from "../store";

let token = "";

const FormView = () => {
  const {
    state: { accountInfo },
  } = useContext(AppContext);
  const [success, setSuccess] = useState(false);
  const { isAuthorized, login } = useIsAuthorized();
  const { formId }: { formId?: string } = useParams();
  let query = isAuthorized ? FORMAUTH_QUERY : FORM_QUERY;
  let dataAttribute = isAuthorized ? "formAuth" : "form";
  const handleSave = async (
    values: any[],
    formikHelpers?: FormikHelpers<any>
  ) => {
    try {
      await submitForm({
        variables: {
          formId,
          payload: { ...values, token },
        },
      });
      setSuccess(true);
    } catch (err: any) {
      console.error(err);
    }
    formikHelpers?.setSubmitting(false);
  };
  const handleReset = (values: any[], formikHelpers?: FormikHelpers<any>) => {
    console.log(values);
    formikHelpers?.setValues({
      reactSelect: 0,
    });
  };
  const [uploadFile, { error: uploadError }] =
    useMutation(FILE_UPLOAD_MUTATION);
  const handleUpload = (formik: any, field: any, event: any) => {
    const files = Array.from(event.target.files as File[]).map(
      (f: File) => ({name:f.name, fileType: f.type})
    );
    formik.setFieldValue(field.name, files);
    files.forEach((file) => {
      uploadFile({
        variables: {
          file: [file],
          formId,
          email:  formik.getFieldValue('user.email'),
        },
      });
    });
  };
  const handleDrop = (
    formik: any,
    field: any,
    accepted: File[]
  ) => {
    formik.setFieldValue(
      field.name,
      accepted.map((f: File) => ({name:f.name, fileType: f.type}))
    );
    accepted.forEach((a) => {
      uploadFile({
        variables: {
          file: a,
          formId,
          email:  formik.getFieldProps('user.email')?.value,
        },
      });
    });
    
  };
  const makeElement = (element: any, trigger: string, callback: Function) => {
    const el = {
      ...element,
    };
    if (trigger && callback) {
      if (!el.options) {
        el.options = {};
      }
      el.options = { ...el.options, [trigger]: callback };
    }
    return el;
  };
  const mapElements = (elements: any): any => {
    return Object.keys(elements).reduce((els, k) => {
      if (elements[k].elements) {
        const newEl = {
          ...elements[k],
          elements: mapElements(elements[k].elements),
        };
        return {
          ...els,
          [k]: newEl,
        };
      }
      if (elements[k].renderer === "file") {
        return {
          ...els,
          [k]: makeElement(elements[k], "onChange", handleUpload),
        };
      }
      if (elements[k].renderer === "file-uploader") {
        return {
          ...els,
          [k]: makeElement(elements[k], "onDrop", handleDrop),
        };
      }
      return {
        ...els,
        [k]: elements[k],
      };
    }, {});
  };
  const addFileUploadHandlers = (config: any) => {
    const els = config.elements ? mapElements(config.elements) : null;
    return { ...config, elements: { ...els } };
  };
  const mutationName = isAuthorized
    ? SUBMITFORMAUTH_MUTATION
    : SUBMITFORM_MUTATION;
  const [submitForm, { loading: submitLoading, error: submitError }] =
    useMutation(mutationName);
  if (success) {
    return (
      <AlertDismissible handleClose={() => setSuccess(false)} variant="success">
        We have received your request. Thank you!
      </AlertDismissible>
    );
  }

  return (
    <>
      {submitLoading && <Loading />}
      {(uploadError || submitError) && (
        <AlertDismissible variant="danger">
          {uploadError?.message || submitError?.message}
        </AlertDismissible>
      )}
      <Query
        dataOut={(data: any) => {
          token = data[dataAttribute].token;
        }}
        loadingLabel="Loading Your Form"
        variables={{ formId }}
        query={query}
        errorView={
          <p>
            You must be logged in to access this form. &nbsp;{" "}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => login(loginRequest, "loginPopup")}
            >
              Login or Sign Up
            </Button>
          </p>
        }
      >
        {({ data }: { data: { [key: string]: FormContainerConfig } }) => {
          const form = data[dataAttribute];
          if (!form) {
            return <span>Not Found.</span>;
          }
          const config: FormProps = {
            ...form.config,
            initialValues: {
              ...form.config?.initialValues,
              user: {
                name: accountInfo?.name,
                email: accountInfo?.username
              },
            },
          };
          // Add required user fields:
          config.schema = {
            ...config.schema,
            elements: {
              _user: {
                type: "container",
                renderer: "fieldset",
                name: "user",
                title: "Who Are You?",
                cardBodyClass: "card-body row-cols-2 row",
                elements: {
                  name: {
                    name: "name",
                    label: "Name",
                    type: "field",
                    renderer: "text",
                    fieldType: "text",
                    htmlClass: "col",
                    validation: [["string"], ["min", 3]],
                  },
                  email: {
                    name: "email",
                    label: "Email (Required)",
                    type: "field",
                    renderer: "text",
                    fieldType: "email",
                    htmlClass: "col",
                    validation: [["string"], ["required"], ["email"]],
                  },
                }
              },
              meta: {
                type: "container",
                renderer: "div",
                elements: {
                  list: {
                    type: "container",
                    as: "div",
                    renderer: "html-tag",
                    elements: {
                      item1: {
                        type: "field",
                        renderer: "html",
                        template: "html",
                        htmlClass: "mt-5 mb-4",
                        as: "h2",
                        htmlString: form.title,
                      },
                      item2: {
                        type: "field",
                        renderer: "html",
                        template: "html",
                        as: "p",
                        htmlClass: "my-4",
                        htmlString: form.description,
                      },
                    },
                  },
                },
              },
              ...config.schema.elements,
            },
          };
          config.onSubmit = handleSave;
          config.onReset = handleReset;
          const handlersAddedEls = addFileUploadHandlers(config.schema);
          const newConfig = {
            ...config,
            schema: {
              ...config.schema,
              ...handlersAddedEls,
            },
          };
          return <FormContainer config={newConfig} />;
        }}
      </Query>
    </>
  );
};

export default FormView;
